import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { HelpButton } from "@/components/help/HelpButton";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, ArrowLeft, Phone, Mail, MapPin, Sparkles, MessageSquare, Bot, User, Clock,
  PhoneCall, Voicemail, StickyNote, PhoneOff, CalendarClock, CheckCircle2, Send, Copy,
  AlertTriangle, GitMerge, HeartHandshake, Layers, DollarSign, Video,
} from "lucide-react";
import RevenueOutcomeDialog from "./RevenueOutcomeDialog";
import { PortalAccessCard } from "./PortalAccessCard";
import { FamilyPlanPanel } from "./FamilyPlanPanel";
import { PortalMessagesCard } from "./PortalMessagesCard";

const pretty = (s) => String(s == null ? "" : s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const QUICK = [
  { key: "call_logged", label: "Call logged", icon: PhoneCall },
  { key: "left_voicemail", label: "Voicemail", icon: Voicemail },
  { key: "no_answer", label: "No answer", icon: PhoneOff },
  { key: "spoke_with_family", label: "Spoke w/ family", icon: HeartHandshake },
  { key: "sent_email", label: "Sent email", icon: Mail },
  { key: "sent_resource", label: "Sent resource", icon: Send },
  { key: "booked_consultation", label: "Booked consult", icon: CalendarClock },
  { key: "referred_to_partner", label: "Referred partner", icon: Layers },
  { key: "needs_follow_up", label: "Needs follow-up", icon: Clock },
  { key: "added_note", label: "Add note", icon: StickyNote },
];
const Field = ({ label, value }) => (
  value ? <div><dt className="text-[11px] uppercase tracking-wide text-[#4B5563]">{label}</dt><dd className="text-[14px] text-[#252221]">{value}</dd></div> : null
);

export default function CrmLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [dups, setDups] = useState([]);
  const [revOpen, setRevOpen] = useState(false);
  const [fu, setFu] = useState({ business_days: "", custom_date: "", will_call_back_date: "", note: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/leads/${id}`);
      setD(data);
      const dd = await api.get(`/crm/leads/${id}/duplicates`);
      setDups(dd.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load lead."); navigate("/crm/pipeline"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const lead = d?.lead;
  const names = d?.user_names || {};

  const doAction = async (action) => {
    if (action === "added_note" && !note.trim()) { toast.error("Type a note first."); return; }
    try { await api.post(`/crm/leads/${id}/action`, { action, note: note.trim() || null }); setNote(""); toast.success("Logged."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const setStage = async (stage) => {
    try { await api.post(`/crm/leads/${id}/stage`, { stage, reason: "Manual change" }); toast.success("Stage updated."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const scheduleFu = async () => {
    const payload = {};
    if (fu.will_call_back_date) payload.will_call_back_date = fu.will_call_back_date;
    else if (fu.custom_date) payload.custom_date = fu.custom_date;
    else if (fu.business_days) payload.business_days = Number(fu.business_days);
    else { toast.error("Pick a follow-up option."); return; }
    payload.note = fu.note || null;
    try { await api.post(`/crm/leads/${id}/follow-up`, payload); toast.success("Follow-up scheduled."); setFu({ business_days: "", custom_date: "", will_call_back_date: "", note: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const merge = async (intoId) => {
    try { await api.post(`/crm/leads/${id}/merge`, { into_id: intoId }); toast.success("Merged."); navigate(`/crm/leads/${intoId}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Merge failed (admin only)."); }
  };

  if (loading || !lead) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  const displayName = [lead.first_name, lead.last_name].filter(Boolean).join(" ") || lead.loved_one_name || "Lead";

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="crm-lead-detail">
      <button onClick={() => navigate("/crm/pipeline")} className="flex items-center gap-1.5 text-[14px] text-[#1C4358] hover:underline mb-3"><ArrowLeft size={15} /> Back to pipeline</button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">{displayName}</h1>
          <div className="flex items-center gap-3 flex-wrap text-[13px] text-[#4B5563] mt-1">
            {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#9C1E22]"><Phone size={12} /> {lead.phone}</a>}
            {lead.email && <span className="flex items-center gap-1"><Mail size={12} /> {lead.email}</span>}
            {(lead.city || lead.state_province) && <span className="flex items-center gap-1"><MapPin size={12} /> {lead.city} {lead.state_province}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton route="/leads" testid="lead-help-button" />
          <span className="text-[11px] text-[#4B5563]">Stage</span>
          <Select value={lead.pipeline_stage || "new_lead"} onValueChange={setStage}>
            <SelectTrigger data-testid="crm-stage-select" className="w-[210px] h-10 text-[13px]"><SelectValue /></SelectTrigger>
            <SelectContent>{(d.stages || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="lead-revenue-outcome-btn" onClick={() => setRevOpen(true)} className="h-10 bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1"><DollarSign size={15} /> Add Revenue Outcome</Button>
          <Button data-testid="lead-schedule-meeting-btn" onClick={() => navigate(`/meetings?new=1&lead=${id}`)} className="h-10 bg-[#1C4358] hover:bg-[#163545] text-white gap-1"><Video size={15} /> Schedule meeting</Button>
        </div>
      </div>

      {lead.follow_up_missed && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-[rgba(156,30,34,0.08)] px-3 py-2 text-[13px] text-[#9C1E22]" data-testid="crm-missed-flag"><AlertTriangle size={15} /> Missed follow-up — please reach out.</div>
      )}
      {dups.length > 0 && (
        <div className="mt-3 rounded-lg bg-[rgba(183,121,31,0.10)] px-3 py-2.5" data-testid="crm-dup-warning">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-[#B7791F]"><Copy size={14} /> {dups.length} possible duplicate(s)</div>
          {dups.map((dp) => (
            <div key={dp.id} className="flex items-center justify-between gap-2 mt-1.5 text-[13px]">
              <span className="text-[#252221]">{dp.name} <span className="text-[#4B5563]">({(dp.match_reasons || []).join(", ")})</span></span>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" className="h-7 text-[12px] text-[#1C4358]" onClick={() => navigate(`/crm/leads/${dp.id}`)}>View</Button>
                <Button size="sm" variant="outline" data-testid={`crm-merge-${dp.id}`} className="h-7 text-[12px] border-[#B7791F]/40 text-[#B7791F] gap-1" onClick={() => merge(dp.id)}><GitMerge size={12} /> Merge into</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <Card className="mt-4 p-4 border-[#E6EDF2]" data-testid="crm-quick-actions">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">One-click actions</div>
        <div className="flex flex-wrap gap-2">
          {QUICK.map((a) => (
            <Button key={a.key} size="sm" variant="outline" data-testid={`crm-action-${a.key}`} onClick={() => doAction(a.key)}
              className="border-[#D6E0E7] text-[#1C4358] hover:bg-[#1C4358]/6 gap-1.5 text-[13px]"><a.icon size={14} /> {a.label}</Button>
          ))}
        </div>
        <Textarea data-testid="crm-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note (attached to your next action)…" className="mt-3 text-[14px] rounded-lg border-[#1C4358]/20" />
      </Card>

      <PortalAccessCard leadId={id} />
      <FamilyPlanPanel leadId={id} />
      <PortalMessagesCard leadId={id} />

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* Schedule follow-up */}
        <Card className="p-4 border-[#E6EDF2] lg:col-span-1" data-testid="crm-followup-card">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><CalendarClock size={13} /> Schedule follow-up</div>
          <div className="flex flex-wrap gap-1.5">
            {[3, 4, 5, 6, 7].map((n) => (
              <button key={n} data-testid={`crm-fu-${n}`} onClick={() => setFu({ business_days: String(n), custom_date: "", will_call_back_date: "", note: fu.note })}
                className={`rounded-full px-3 h-8 text-[13px] font-medium border transition-colors ${fu.business_days === String(n) ? "border-[#9C1E22] bg-[#9C1E22]/8 text-[#1C4358]" : "border-[#1C4358]/20 text-[#4B5563]"}`}>{n} biz days</button>
            ))}
          </div>
          <label className="text-[11px] text-[#4B5563] mt-3 block">Custom date</label>
          <Input type="date" data-testid="crm-fu-custom" value={fu.custom_date} onChange={(e) => setFu({ ...fu, custom_date: e.target.value, business_days: "", will_call_back_date: "" })} className="h-9 text-[13px]" />
          <label className="text-[11px] text-[#4B5563] mt-2 block">"They will call back" date</label>
          <Input type="date" data-testid="crm-fu-callback" value={fu.will_call_back_date} onChange={(e) => setFu({ ...fu, will_call_back_date: e.target.value, business_days: "", custom_date: "" })} className="h-9 text-[13px]" />
          <Input data-testid="crm-fu-note" value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })} placeholder="Note (optional)" className="mt-2 h-9 text-[13px]" />
          <Button data-testid="crm-fu-save" onClick={scheduleFu} className="mt-3 w-full bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Clock size={14} /> Schedule</Button>
          {(d.follow_ups || []).filter((f) => f.status === "scheduled").length > 0 && (
            <div className="mt-3 space-y-1 border-t border-[#F0F4F7] pt-2">
              {(d.follow_ups || []).filter((f) => f.status === "scheduled").map((f) => (
                <div key={f.id} className="flex items-center justify-between text-[12px]"><span className="text-[#4B5563]">{f.due_date}{f.will_call_back ? " (call back)" : ""}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] text-[#2F7D5A]" onClick={async () => { await api.post(`/crm/follow-ups/${f.id}/complete`); load(); }}>Done</Button></div>
              ))}
            </div>
          )}
        </Card>

        {/* Record */}
        <Card className="p-4 border-[#E6EDF2] lg:col-span-2" data-testid="crm-record">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Lead record</div>
          {lead.ai_summary && (
            <div className="rounded-lg bg-[#FBF6EC] border border-[#1C4358]/10 p-2.5 text-[13px] mb-3 flex items-start gap-2"><Sparkles size={14} className="text-[#9C1E22] mt-0.5 shrink-0" /><span><strong className="text-[#1C4358]">AI summary:</strong> {lead.ai_summary}</span></div>
          )}
          <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
            <Field label="Loved one" value={lead.loved_one_name} />
            <Field label="Service lane" value={pretty(lead.service_interest) || lead.matched_service_lane} />
            <Field label="Secondary lanes" value={(lead.secondary_service_lanes || []).map(pretty).join(", ")} />
            <Field label="Urgency" value={pretty(lead.urgency_level)} />
            <Field label="Source" value={pretty(lead.lead_source)} />
            <Field label="Campaign" value={lead.campaign_source} />
            <Field label="CTA clicked" value={lead.cta_clicked} />
            <Field label="Page path" value={lead.page_path} />
            <Field label="Reverse-search topic" value={lead.reverse_search_topic} />
            <Field label="A/B variation" value={lead.ab_variation != null ? `Variation ${String.fromCharCode(65 + lead.ab_variation)}` : null} />
            <Field label="Thank-you shown" value={lead.thank_you_type} />
            <Field label="Assigned to" value={names[lead.assigned_to_user_id]} />
            <Field label="Opened by" value={names[lead.opened_by_user_id]} />
            <Field label="Managed by" value={names[lead.managed_by_user_id]} />
            <Field label="Closed by" value={names[lead.closed_by_user_id]} />
            <Field label="Deal value" value={lead.deal_value != null ? `$${Number(lead.deal_value).toLocaleString()}` : null} />
            <Field label="Revenue status" value={pretty(lead.revenue_status)} />
          </dl>
          {(lead.sub_statuses || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">{lead.sub_statuses.map((s) => <Badge key={s} variant="outline" className="text-[11px] border-[#B7791F]/30 text-[#B7791F]">{pretty(s)}</Badge>)}</div>
          )}
          {lead.chat_session_id && (
            <Button size="sm" variant="outline" className="mt-3 border-[#D6E0E7] text-[#1C4358] gap-1" onClick={() => navigate(`/chat?session=${lead.chat_session_id}`)}><MessageSquare size={14} /> View chat transcript</Button>
          )}
          {Object.keys(lead.intake_answers || {}).length > 0 && (
            <div className="mt-3 border-t border-[#F0F4F7] pt-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-1">Intake answers ({pretty(lead.intake_situation)})</div>
              <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
                {Object.entries(lead.intake_answers).map(([k, v]) => <Field key={k} label={pretty(k)} value={String(v)} />)}
              </dl>
            </div>
          )}
        </Card>
      </div>

      {/* Activity timeline */}
      <Card className="p-4 border-[#E6EDF2] mt-4" data-testid="crm-activity">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Activity</div>
        {(d.communications || []).length === 0 && (lead.stage_history || []).length === 0 ? <p className="text-[13px] text-[#4B5563]">No activity yet.</p> : (
          <ul className="space-y-2">
            {(d.communications || []).map((c) => (
              <li key={c.id} className="flex items-start gap-2 text-[13px]"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#1C4358] shrink-0" /><span><strong className="text-[#1C4358]">{c.subject}</strong>{c.summary && c.summary !== c.subject ? ` — ${c.summary}` : ""} <span className="text-[#9AA5B1]">· {(c.created_at || "").slice(0, 16).replace("T", " ")} · {c.created_by_name || ""}</span></span></li>
            ))}
            {[...(lead.stage_history || [])].reverse().slice(0, 8).map((h, i) => (
              <li key={`h${i}`} className="flex items-start gap-2 text-[13px]"><span className="mt-1">{h.by === "system" ? <Bot size={12} className="text-[#2C6480]" /> : <User size={12} className="text-[#4B5563]" />}</span><span className="text-[#4B5563]">Stage → <strong className="text-[#1C4358]">{pretty(h.to)}</strong> ({h.by === "system" ? "auto" : h.by_name}) <span className="text-[#9AA5B1]">· {(h.at || "").slice(0, 16).replace("T", " ")}</span></span></li>
            ))}
          </ul>
        )}
      </Card>

      <RevenueOutcomeDialog open={revOpen} onOpenChange={setRevOpen} leadId={id} contextLabel={displayName}
        defaults={{ name: displayName, service_lane: lead.service_interest, actual_value: lead.deal_value,
          opened_by_user_id: lead.opened_by_user_id, managed_by_user_id: lead.managed_by_user_id,
          closed_by_user_id: lead.closed_by_user_id }}
        onSaved={load} />
    </div>
  );
}
